import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from '@mui/icons-material/Info';
import Typography from "@mui/material/Typography";
import { getQuestionTemplate } from "../../../actions/questionTemplateActions";
import ArgoChipHook from "../../common/ArgoChipHook";
import AnswerViewerHook from "../answers/AnswerViewerHook";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import statusFormatter from "../../../utilities/StatusFormatter";
import * as metadataUtils from "../../../utilities/metadata";
import * as TEMPLATE from "../../../constants/templateConstants";
import * as questionType from "../../../constants/questionTypeConstants";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities";
import * as questionTypes from "../../../constants/questionTypeConstants";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";


const QuestionReviewHook = (props) => {
    const {
        templateKey,
        templateId,
        scrollTo,
        hidePortalPreview,
        setStateCallBack = () => {}
    } = props;

    const [state, setState] = useState({
        initialScroll: false,
        openGroupReview: false,
        selectedGroupInfoKey: "",
        selectedGroupInfoId: "",
        selectedGroupInfoTemplate: "",
        showGroupBackButton: true
    });

    const [getTemplateCalledFlag, setGetTemplateCalledFlag] = useState(false);
    const [templateLoadedFlag, setTemplateLoadedFlag] = useState(false);
    const [questionTemplate, setQuestionTemplate] = useState({});

    const dispatch = useDispatch();
    const questionTemplates = useSelector(state => state.questionTemplate);
    const template = useSelector(state => state.questionTemplate.hasOwnProperty(templateKey) 
        ? state.questionTemplate[templateKey] 
        : {});
    const answerOptionTemplates = useSelector(state => state.questionTemplate.hasOwnProperty(templateKey) 
        ? state.questionTemplate[templateKey].answerOptionTemplates 
        : {});

    // ComponentWillMount equivalent
    useEffect(() => {
        dispatch(addDrawerNameToStack("Review", "Question"))

        // componentWillUnmount equivalent
        return () => {
            dispatch(removeDrawerNameToStack())
        };
    },[dispatch]);

    useEffect(() => {

        if (!templateLoadedFlag) {
            if (templateKey && questionTemplates.hasOwnProperty(templateKey) && questionTemplates[templateKey].id === parseInt(templateId)) {

                setQuestionTemplate(questionTemplates[templateKey]);
                setTemplateLoadedFlag(true);

            } else if (templateKey || templateId) {
                if (!getTemplateCalledFlag) {
                    setGetTemplateCalledFlag(true);
                    dispatch(getQuestionTemplate(templateKey, templateId)); // Get question will always get by ID if ID is passed to action
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateKey, templateId, questionTemplates, template, dispatch]);

    // Handle initial scroll
    useEffect(() => {
        if (!state.initialScroll && scrollTo) {
            const page = document.getElementById(scrollTo);
            if (page) {
                setTimeout(function () {
                    page.scrollIntoView({ block: 'end',  behavior: 'smooth' });
                }, 100);
                setState(prevState => ({
                    ...prevState,
                    initialScroll: true
                }));
            }
        }
    }, [scrollTo, state.initialScroll]);

    // Notify parent of showGroupBackButton state
    useEffect(() => {
        setStateCallBack({ showGroupBackButton: state.showGroupBackButton });
    }, [state.showGroupBackButton, setStateCallBack]);

    const setStateCallback = (stateObj) => {
        setState(prevState => ({
            ...prevState,
            ...stateObj
        }));
    };

    const handleShowGroupBackButtonClick = (event, fieldName, buttonTitle) => {
        if (buttonTitle === 'BACK') {
            setState(prevState => ({
                ...prevState,
                openGroupReview: false, 
                showGroupBackButton: true
            }));
        }
    };

    // Clone template for consistency with original component
    let templateClone = {};

    if (templateLoadedFlag) {
        templateClone = portalShared.cloneObj(questionTemplate);
    }
    // else if (template.hasOwnProperty("id") && template.id === parseInt(templateId)) {
    //     templateClone = {...template};
    // }

    // If in group review mode, render the group review template
    if (state.openGroupReview) {
        return (
            <ArgoReviewTemplateHook
                templateType={TEMPLATE.TYPE.GROUP}
                templateKey={state.selectedGroupInfoKey}
                handleToolbarButtonClickFunction={handleShowGroupBackButtonClick}
                width={props.width}
            />
        );
    }

    // Setup display modifiers
    let displayAsLikert = "";
    let metadataValueObj = null;
    let radioQuestionTypeDisplayModifier = "";
    
    if (templateClone.questionType === questionTypes.RADIO) {
        // If question is of type RADIO then see if it is being displayed as a LIKERT
        if (templateClone.metadata?.hasOwnProperty(portalShared.CUSTOM_DDO_QUE_FORMAT)) {
            metadataValueObj = JSON.parse(templateClone.metadata[portalShared.CUSTOM_DDO_QUE_FORMAT]);
            if (metadataValueObj.hasOwnProperty("displayAs")) {
                displayAsLikert = metadataValueObj["displayAs"];
                if (displayAsLikert === "likert") {
                    radioQuestionTypeDisplayModifier = "(Displayed as a Likert scale)";
                }
            }
        }
    }

    // Get reporting category
    let reportingCategory = "";
    if (templateClone.hasOwnProperty("metadata")) {
        if (templateClone.metadata.hasOwnProperty(portalShared.KEY_METRIC_CATEGORY_QUESTION)) {
            reportingCategory = templateClone.metadata[portalShared.KEY_METRIC_CATEGORY_QUESTION];
        }
    }

    // Get tags
    let tags = templateClone.searchTags || [];

    const standardStyle = {
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        overflowWrap: "break-word"
    };

    return (
        templateClone.hasOwnProperty("metadata") ?
            <div style={{flexGrow: "2", padding: "32px"}}>

                <div style={{minHeight: "min-content"}}>

                    <Typography variant="h6" component="div">
                        Question Information:
                    </Typography>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-complex-question-type-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Status:</div>
                        <div>&nbsp;</div>
                        <div>{statusFormatter(templateClone, 0, 0, true)}</div>
                        <div>&nbsp;&nbsp;</div>
                        <div style={{color: "red", fontSize: "12px", paddingTop: "2px"}}>{templateClone.currentFlag ? "" : "(Not Current Version)"}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-question-type-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Type:</div>
                        <div>&nbsp;</div>
                        <div>{questionTypes.getLabel[templateClone.questionType] + " " + radioQuestionTypeDisplayModifier}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-question-name-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Question:</div>
                        <div>&nbsp;</div>
                        <div>{templateClone.name}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-question-qualifier-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Qualifier:</div>
                        <div>&nbsp;</div>
                        <div>{templateClone.nameQualifier}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-question-description-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Description:</div>
                        <div>&nbsp;</div>
                        <div>
                            {templateClone.description && <div style={standardStyle}>
                                <InfoIcon style={{minWidth: "36px"}}/>
                                <div>&nbsp;</div>
                                <div>{templateClone.description}</div>
                            </div>}
                        </div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-key-metric-category-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Key Metric Classification:</div>
                        <div>&nbsp;</div>
                        <div>{reportingCategory}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-optional-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Response:</div>
                        <div>&nbsp;</div>
                        <div style={{color: templateClone.optional ? "black" : "red"}}>{templateClone.optional ? "Optional" : "Required"}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-tags-label-container-div" style={standardStyle}>
                        <div style={{color: "darkgray"}}>Assigned Tags:</div>
                        <div>&nbsp;</div>
                        <div>{tags.length === 0 ? " No assigned tags." : ""}</div>
                    </div>

                    <div id="QuestionReview-tags-container-div" style={standardStyle}>
                        <div id="QuestionReview-tags-container-div-spacer" style={{height: "45px", width: "100%",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            overflowWrap: "break-word"}}>
                            {tags.map((tag, index) => {
                                return(<ArgoChipHook
                                        key={"ArgoChipHook-" + index}
                                        fileName="tag-lookup"
                                        fieldName={index.toString()}
                                        index={index}
                                        label={tag.name}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="QuestionReview-metadata-container-div">
                        {metadataUtils.listMetadata(templateClone, "Question")}
                    </div>

                    {/*// ===========================================================================================================*/}
                    {/*// ANSWERS LIST */}
                    {/*// ===========================================================================================================*/}

                    {(templateClone.answerOptionTemplates?.length > 0) && (
                        <AnswerViewerHook
                            parentName={"QuestionReview"}
                            answerOptionTemplates={templateClone.answerOptionTemplates}
                            setStateCallback={setStateCallback}
                        />
                    )}

                    {/*// ===========================================================================================================*/}
                    {/*// PORTAL PREVIEW */}
                    {/*// ===========================================================================================================*/}

                    {!hidePortalPreview && templateClone.questionType !== questionTypes.LIKERT_RADIO && (
                        <div>
                            <ArgoPortalPreviewHook
                                calledBy="QuestionReview"
                                templateType={TEMPLATE.TYPE.QUESTION}
                                template={templateClone}
                                answerOptionTemplates={answerOptionTemplates}
                                hidePortalPreview={templateClone.questionType === questionType.LIKERT_RADIO}
                            />
                        </div>
                    )}

                </div>
            </div>
            : ""
    );
};

QuestionReviewHook.defaultProps = {
    scrollTo: "",
    setStateCallBack: function () {},
    questionTemplates: {},
    template: {},
    templateIsGraph: false
};

QuestionReviewHook.propTypes = {
    templateKey: PropTypes.string,
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    template: PropTypes.object,
    templateIsGraph: PropTypes.bool,
    questionTemplates: PropTypes.object,
    scrollTo: PropTypes.string,
    setStateCallBack: PropTypes.func,
    hidePortalPreview: PropTypes.bool,
    width: PropTypes.number
};

export default QuestionReviewHook;